import { ExporterDatasetJobsEndpoint } from "src/api/endpoints";
import { DATASET_JOB_TIMEOUT } from "src/datasets/utils";
import { toastActions } from "src/design-system/Toast/utils";
import {
  DownloadDatasetJob,
  ExportDecisionsDatasetJob,
  HistoryExportDatasetJob,
  SimulationDownloadDatasetJob,
} from "src/jobs/types";
import { copyTextToClipboard } from "src/utils/clipboard";

export const informPreparingDownload = (toastId: string) => {
  toastActions.loading({
    id: toastId,
    title: "Preparing your download...",
    description:
      "This could take a few minutes. The process will continue in the background even when this notification is dismissed.",
    duration: Infinity,
  });
};

export const pollAndNotifyDownloadSuccess = async (
  datasetJob:
    | DownloadDatasetJob
    | HistoryExportDatasetJob
    | ExportDecisionsDatasetJob
    | SimulationDownloadDatasetJob,
  baseUrl: string,
  toastId: string,
  pollEndpoint: typeof ExporterDatasetJobsEndpoint.getDatasetJob = ExporterDatasetJobsEndpoint.getDatasetJob,
) => {
  while (datasetJob.status === "PENDING") {
    await new Promise((r) => setTimeout(r, 1000));
    datasetJob = (await pollEndpoint({
      baseURL: baseUrl,
      id: datasetJob.id,
    })) as typeof datasetJob;
  }
  if (datasetJob.status === "COMPLETED") {
    const s3Url = datasetJob.response?.s3_url;
    if (!s3Url) {
      toastActions.failure({
        id: toastId,
        duration: Infinity,
        title: "Download failed",
        description: "The download URL is missing.",
      });
      return;
    }
    toastActions.success({
      id: toastId,
      duration: Infinity,
      title: "Download ready",
      description: `Your ${datasetJob.request.format.toUpperCase()} file is ready for download.`,
      actionText: "Click here to download",
      onActionClick: () => window.open(s3Url, "_blank"),
    });
  } else {
    toastActions.failure({
      id: toastId,
      duration: Infinity,
      title: "Download failed",
      description: datasetJob.error?.message ?? DATASET_JOB_TIMEOUT,
    });
  }
};

export const informPreparingLink = (toastId: string) => {
  toastActions.loading({
    id: toastId,
    title: "Preparing your link...",
    description:
      "This could take a few minutes. The process will continue in the background even when this notification is dismissed.",
    duration: Infinity,
  });
};

export const pollAndNotifyLinkSuccess = async (
  datasetJob:
    | DownloadDatasetJob
    | HistoryExportDatasetJob
    | ExportDecisionsDatasetJob
    | SimulationDownloadDatasetJob,
  baseUrl: string,
  toastId: string,
  pollEndpoint: typeof ExporterDatasetJobsEndpoint.getDatasetJob = ExporterDatasetJobsEndpoint.getDatasetJob,
) => {
  while (datasetJob.status === "PENDING") {
    await new Promise((r) => setTimeout(r, 1000));
    datasetJob = (await pollEndpoint({
      baseURL: baseUrl,
      id: datasetJob.id,
    })) as typeof datasetJob;
  }
  if (datasetJob.status === "COMPLETED") {
    const s3Url = datasetJob.response?.s3_url;
    toastActions.success({
      id: toastId,
      duration: Infinity,
      title: "Link ready",
      description: `Your ${datasetJob.request.format.toUpperCase()} link is ready.`,
      actionText: "Click here to copy",
      onActionClick: () => copyTextToClipboard(s3Url || ""),
    });
  } else {
    toastActions.failure({
      id: toastId,
      duration: Infinity,
      title: "Link generation failed",
      description: datasetJob.error?.message ?? DATASET_JOB_TIMEOUT,
    });
  }
};
